import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { AdPlacementsGridPreviewPanel } from '@bingads-webui-campaign-react/ad-placements-grid-preview';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { getGridPreviewCarouselStyle, getAdPlacementStyle } from './styles';
import { Header } from '../header';
import { ValidationMessage } from '../validation-message';

const AllPlacementsPanel = ({
  isOpen,
  onDismiss,
  className,
  cellsNumberPerRow,
  totalCount,
  onRenderCell,
  cellHeight,
}) => {
  const classes = useDefaultStyles(getGridPreviewCarouselStyle);

  const props = {
    isOpen,
    onDismiss,
    cellsNumberPerRow,
    totalCount,
    onRenderCell,
    cellHeight,
  };

  return (
    <div >
      <AdPlacementsGridPreviewPanel
        {...props}
        className={classnames(classes.gridPreviewContainer, className)}
      />
    </div>
  );
};

AllPlacementsPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  cellsNumberPerRow: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onRenderCell: PropTypes.func.isRequired,
  className: PropTypes.string,
  cellHeight: PropTypes.number,
};

AllPlacementsPanel.defaultProps = {
  className: '',
  cellHeight: 482,
};

export const useAllPlacementsPanel = ({
  previewElements,
  getSelectedImage,
  headerButtonProps,
  getCarouselTitleByIndex,
  isImageValid,
  getIsRequiredByIndex,
  getMinSizeByIndex,
  styleOverrides,
  shouldHideHeaderButtonsByIndex,
  getTemplateTypeByIndex,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getAdPlacementStyle);

  const onDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onRenderCell = useCallback((index) => {
    const previewElement = previewElements[index];
    const selectedImage = getSelectedImage(index);

    return (
      <div className={classnames('view-all-preview-element', classes.cellRoot)}>
        <Header
          index={index}
          selectedImage={selectedImage}
          getCarouselTitleByIndex={getCarouselTitleByIndex}
          headerButtonProps={headerButtonProps}
          getIsRequiredByIndex={getIsRequiredByIndex}
          isImageValid={isImageValid}
          styleOverrides={{
            header: {
              actionButtons: {
                '& .edit-image-button': {
                  marginRight: 8,
                },
              },
            },
            ...styleOverrides,
          }}
          shouldHideHeaderButtonsByIndex={shouldHideHeaderButtonsByIndex}
          getTemplateTypeByIndex={getTemplateTypeByIndex}
        />
        <ValidationMessage
          index={index}
          selectedImage={selectedImage}
          isImageValid={isImageValid}
          getMinSizeByIndex={getMinSizeByIndex}
          styleOverrides={styleOverrides}
        />
        <div className={classnames('placement-root', classes.placementRoot)}>
          {previewElement}
        </div>
      </div>
    );
  }, [
    classes,
    previewElements,
    getSelectedImage,
    headerButtonProps,
    getCarouselTitleByIndex,
    isImageValid,
    getMinSizeByIndex,
    styleOverrides,
    getIsRequiredByIndex,
    shouldHideHeaderButtonsByIndex,
    getTemplateTypeByIndex,
  ]);

  // eslint-disable-next-line arrow-body-style
  const panelElement = useMemo(() => {
    return (
      <AllPlacementsPanel
        isOpen={isOpen}
        onDismiss={onDismiss}
        className="view-all-placements-panel"
        cellsNumberPerRow={2}
        totalCount={previewElements.length}
        onRenderCell={onRenderCell}
        cellHeight={510}
      />
    );
  }, [isOpen, onDismiss, previewElements, onRenderCell]);

  // eslint-disable-next-line arrow-body-style
  const footerButtonProps = useMemo(() => {
    return {
      onClick: () => setIsOpen(true),
      onRenderIcon: () => (
        <span className="iconba iconba-FullView" />
      ),
      buttonText: i18n.getString(_TL_('View example placements')),
      buttonClassName: 'view-placements',
    };
  }, [i18n]);

  return {
    panelElement,
    footerButtonProps,
  };
};
